/**
 * The printable age sheet — the same document shape as the বাতা tracking sheet
 * (see bhata-tracking/bhataTrackingTemplate.js): brand header, a hero with the
 * answer, info-card sections and a footer. It is rendered into an iframe, for
 * `window.print()` and for the PDF / picture exports, so everything is inline
 * CSS with no app classes — that iframe loads none of the app's stylesheets.
 */

const BRAND = {
    name: 'SohozKaj',
    url: 'sohozkaj.com',
    primary: '#ea6b0e',
    primaryDark: '#c2560a',
    ink: '#1c1815',
    muted: '#6b635c',
    line: '#ece8e4',
    tint: '#fff4e6',
    surface: '#ffffff',
    page: '#ffffff',
};

/** A4 printable width at 96dpi with 12mm margins. */
export const EXPORT_PAGE_WIDTH_PX = Math.round(((210 - 24) / 25.4) * 96);

export function getAgeCalculatorLogoUrl(origin, language = 'bn') {
    const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}/images/${language === 'en' ? 'logo-en.svg' : 'logo.svg'}`;
}

function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function buildInfoCards(items) {
    return items
        .filter(([, value]) => value !== '' && value !== null && value !== undefined)
        .map(([label, value, dir]) => `
            <div class="sk-info">
                <span class="sk-info-label">${escapeHtml(label)}</span>
                <span class="sk-info-value"${dir ? ` dir="${escapeHtml(dir)}"` : ''}>${escapeHtml(value)}</span>
            </div>
        `)
        .join('');
}

function buildSection(section) {
    if (!section?.items?.length) return '';
    return `
        <section class="sk-section">
            <h2 class="sk-section-title">${escapeHtml(section.title)}</h2>
            ${section.subtitle ? `<p class="sk-section-sub">${escapeHtml(section.subtitle)}</p>` : ''}
            <div class="sk-info-grid${section.columns === 3 ? ' sk-info-grid-3' : ''}">${buildInfoCards(section.items)}</div>
        </section>
    `;
}

/** A titled note — the day's historical event, the rashifol. */
function buildNote(note) {
    if (!note) return '';
    return `
        <section class="sk-section">
            <h2 class="sk-section-title">${escapeHtml(note.title)}</h2>
            <div class="sk-note">
                <div class="sk-note-head">
                    <strong class="sk-note-heading">${escapeHtml(note.heading)}</strong>
                    ${note.chip ? `<span class="sk-note-chip">${escapeHtml(note.chip)}</span>` : ''}
                </div>
                ${note.meta ? `<span class="sk-note-meta">${escapeHtml(note.meta)}</span>` : ''}
                ${note.text ? `<p class="sk-note-text">${escapeHtml(note.text)}</p>` : ''}
            </div>
        </section>
    `;
}

/**
 * @param {{
 *   sheet: {
 *     hero: { label: string, value: string, chips: string[] },
 *     sections: Array<{ title: string, subtitle?: string, columns?: 2|3, items: Array<[string, string, string?]> }>,
 *     notes: Array<{ title: string, heading: string, chip?: string, meta?: string, text?: string } | null>,
 *     compare?: { title: string, value: string, caption?: string, items: Array<[string, string]> } | null,
 *   },
 *   labels: { title: string, documentTagline: string, generatedOn: string, documentDisclaimer: string },
 *   logoUrl?: string,
 * }} params
 */
export function buildAgeCalculatorBody({ sheet, labels, logoUrl }) {
    const resolvedLogoUrl = logoUrl || '/images/logo.svg';
    const { hero, sections = [], notes = [], compare } = sheet;

    return `
        <div class="sk-doc">
            <header class="sk-header">
                <div class="sk-brand">
                    <img
                        src="${escapeHtml(resolvedLogoUrl)}"
                        alt="${escapeHtml(BRAND.name)}"
                        class="sk-logo-img"
                        crossorigin="anonymous"
                    />
                </div>
                <div class="sk-header-meta">
                    <div class="sk-doc-title">${escapeHtml(labels.title)}</div>
                    <div class="sk-doc-tagline">${escapeHtml(labels.documentTagline)}</div>
                </div>
            </header>

            <section class="sk-hero">
                <div class="sk-hero-label">${escapeHtml(hero.label)}</div>
                <div class="sk-hero-status">${escapeHtml(hero.value)}</div>
                ${hero.chips?.length ? `
                <div class="sk-hero-chips">
                    ${hero.chips.map((chip) => `<span class="sk-hero-id">${escapeHtml(chip)}</span>`).join('')}
                </div>
                ` : ''}
            </section>

            ${sections.map(buildSection).join('')}

            ${compare ? `
            <section class="sk-section">
                <h2 class="sk-section-title">${escapeHtml(compare.title)}</h2>
                <div class="sk-compare">
                    ${compare.caption ? `<span class="sk-compare-caption">${escapeHtml(compare.caption)}</span>` : ''}
                    <strong class="sk-compare-value">${escapeHtml(compare.value)}</strong>
                </div>
                <div class="sk-info-grid">${buildInfoCards(compare.items || [])}</div>
            </section>
            ` : ''}

            ${notes.map(buildNote).join('')}

            <footer class="sk-footer">
                <p class="sk-footer-source">${escapeHtml(labels.generatedOn)}</p>
                <p class="sk-footer-disclaimer">${escapeHtml(labels.documentDisclaimer)}</p>
            </footer>
        </div>
    `;
}

/**
 * The sheet's own rules, all under `.sk-doc`, so the capture can mount the sheet
 * in the app's page without touching anything else there.
 */
export function ageCalculatorSheetStyles() {
    return `
        .sk-doc,.sk-doc *{box-sizing:border-box;margin:0;padding:0;}
        .sk-doc{
            line-height:normal;
            font-family:'Hind Siliguri','Noto Sans Bengali','Segoe UI',system-ui,-apple-system,sans-serif;
            color:${BRAND.ink};
            background:${BRAND.page};
            width:${EXPORT_PAGE_WIDTH_PX}px;
            max-width:${EXPORT_PAGE_WIDTH_PX}px;
            margin:0 auto;
        }
        .sk-header{
            display:flex;align-items:center;justify-content:space-between;gap:16px;
            padding-bottom:16px;border-bottom:3px solid ${BRAND.primary};
            break-inside:avoid;page-break-inside:avoid;
        }
        .sk-logo-img{width:170px;height:auto;max-height:42px;object-fit:contain;object-position:left center;display:block;}
        .sk-header-meta{text-align:right;}
        .sk-doc-title{font-size:15px;font-weight:700;}
        .sk-doc-tagline{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;color:${BRAND.muted};margin-top:2px;}

        .sk-hero{
            margin-top:18px;text-align:center;padding:20px 16px;
            border:1px solid #ffd9ad;background:linear-gradient(180deg,#fff8ef,${BRAND.tint});
            border-radius:14px;break-inside:avoid;page-break-inside:avoid;
        }
        .sk-hero-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:${BRAND.muted};}
        .sk-hero-status{font-size:24px;font-weight:900;margin-top:6px;line-height:1.3;color:${BRAND.primaryDark};}
        .sk-hero-chips{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;margin-top:10px;}
        .sk-hero-id{
            display:inline-block;padding:5px 14px;border-radius:999px;
            background:#ffffff;border:1px solid #ffd9ad;color:${BRAND.primaryDark};
            font-size:12px;font-weight:800;
        }

        .sk-section{margin-top:18px;break-inside:avoid;page-break-inside:avoid;}
        .sk-section-title{
            font-size:12px;font-weight:800;margin-bottom:10px;padding-left:10px;
            border-left:4px solid ${BRAND.primary};text-transform:uppercase;letter-spacing:0.04em;
            break-after:avoid;page-break-after:avoid;
        }
        .sk-section-sub{font-size:10px;font-weight:600;color:#8c847d;margin:-4px 0 8px 14px;}
        .sk-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
        .sk-info-grid-3{grid-template-columns:1fr 1fr 1fr;}
        .sk-info{
            border:1px solid ${BRAND.line};background:${BRAND.surface};border-radius:10px;padding:10px 12px;
            break-inside:avoid;page-break-inside:avoid;
        }
        .sk-info-label{display:block;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#8c847d;}
        .sk-info-value{display:block;margin-top:3px;font-size:13px;font-weight:700;word-break:break-word;}
        .sk-info-value[dir="rtl"]{text-align:left;unicode-bidi:isolate;}

        .sk-compare{
            display:flex;flex-direction:column;align-items:center;gap:2px;margin-bottom:8px;padding:12px;
            border:1px solid #ffd9ad;background:${BRAND.tint};border-radius:10px;
        }
        .sk-compare-caption{font-size:10px;font-weight:700;color:${BRAND.muted};}
        .sk-compare-value{font-size:16px;font-weight:900;color:${BRAND.primaryDark};}

        .sk-note{border:1px solid ${BRAND.line};border-radius:10px;padding:12px 14px;background:${BRAND.surface};}
        .sk-note-head{display:flex;align-items:center;justify-content:space-between;gap:10px;}
        .sk-note-heading{font-size:13px;font-weight:800;}
        .sk-note-chip{
            display:inline-block;padding:2px 9px;border-radius:999px;border:1px solid #ffd9ad;
            background:${BRAND.tint};color:${BRAND.primaryDark};
            font-size:9px;font-weight:800;letter-spacing:0.05em;white-space:nowrap;
        }
        .sk-note-meta{display:block;margin-top:3px;font-size:10px;font-weight:700;color:#8c847d;}
        .sk-note-text{margin-top:6px;font-size:11.5px;line-height:1.6;color:${BRAND.ink};}

        .sk-footer{
            margin-top:22px;padding-top:12px;border-top:1px solid ${BRAND.line};text-align:center;
            break-inside:avoid;page-break-inside:avoid;
        }
        .sk-footer-source{font-size:10px;color:${BRAND.muted};font-weight:600;}
        .sk-footer-disclaimer{margin-top:6px;font-size:9px;line-height:1.55;color:#a8a099;}
    `;
}

export function ageCalculatorStyles() {
    return `
        @page {
            size: A4 portrait;
            margin: 12mm;
        }
        html,body{background:${BRAND.page};}
        ${ageCalculatorSheetStyles()}
        @media print{
            html,body{background:#fff;}
            .sk-doc{width:100%;max-width:100%;background:#fff;}
        }
    `;
}

export function buildAgeCalculatorPrintHtml(params) {
    const { labels } = params;
    return `<!DOCTYPE html><html lang="bn"><head>
        <meta charset="utf-8" />
        <title>${escapeHtml(labels.title)}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&display=swap" rel="stylesheet" />
        <style>${ageCalculatorStyles()}
            body{background:#fff;padding:0;margin:0;}
        </style>
    </head><body>${buildAgeCalculatorBody(params)}</body></html>`;
}
